// components/UpcomingEvents.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, AlertCircle, Settings, Play, Pause } from 'lucide-react';
import useEventsParams from '../hooks/useEventsParams';

const UpcomingEvents = () => {
  const { eventsByCategory, loading, error } = useEventsParams();
  const [currentIndices, setCurrentIndices] = useState([0, 0, 0]);
  const [isPlaying, setIsPlaying] = useState(true); // 🔹 Bouton Play/Pause global

  useEffect(() => {
    if (!loading && !error && eventsByCategory && eventsByCategory.length > 0 && isPlaying) {
      const timer = setInterval(() => {
        setCurrentIndices((prevIndices) =>
          prevIndices.map((index, i) => {
            const categoryEvents = eventsByCategory[i] || [];
            if (categoryEvents.length === 0) return 0;
            return index === categoryEvents.length - 1 ? 0 : index + 1;
          })
        );
      }, 7000);

      return () => clearInterval(timer);
    }
  }, [loading, error, eventsByCategory, isPlaying]);

  const togglePlayPause = () => setIsPlaying((prev) => !prev);

  // -------------------- ÉTATS DE CHARGEMENT / ERREUR / VIDE --------------------
  if (loading || error || !eventsByCategory || eventsByCategory.length === 0) {
    const state = loading ? 'loading' : error ? 'error' : 'empty';
    const bgColor =
      state === 'error'
        ? 'bg-gradient-to-r from-red-500 to-red-600'
        : state === 'loading'
        ? 'bg-gray-200 animate-pulse'
        : 'bg-gradient-to-r from-gray-400 to-gray-500';
    const content =
      state === 'loading'
        ? null
        : state === 'error'
        ? (
          <div className="text-center text-white text-sm">
            <AlertCircle size={20} className="mx-auto mb-1" />
            <div className="font-semibold">Erreur de chargement</div>
            <div className="opacity-90 text-xs">Impossible de récupérer les événements</div>
          </div>
        ) : (
          <div className="text-center text-white text-sm">
            <Settings size={20} className="mx-auto mb-1" />
            <div className="font-semibold">Aucun événement configuré</div>
            <div className="opacity-90 text-xs">Ajoutez des événements dans les paramètres</div>
          </div>
        );

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[#246990]" />
            <h2 className="text-lg font-bold text-[#246990]">Événements à venir</h2>
          </div>
        </div>
        <div className={`h-24 relative overflow-hidden ${bgColor} p-3 rounded-lg flex items-center justify-center`}>
          {content}
        </div>
      </div>
    );
  }

  // -------------------- AFFICHAGE NORMAL --------------------
  const hasEvents = eventsByCategory.some((cat) => cat && cat.length > 0);

  if (!hasEvents) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[#246990]" />
            <h2 className="text-lg font-bold text-[#246990]">Événements à venir</h2>
          </div>
        </div>
        <div className="h-24 relative overflow-hidden">
          <div className="bg-gradient-to-r from-gray-400 to-gray-500 p-3 rounded-lg text-white text-sm h-full flex items-center justify-center">
            <div className="text-center">
              <Settings size={20} className="mx-auto mb-1" />
              <div className="font-semibold">Aucun événement configuré</div>
              <div className="opacity-90 text-xs">Ajoutez des événements dans les paramètres</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 🔹 Titre + Bouton Play/Pause */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-6 h-6 text-[#246990]" />
          <h2 className="text-lg font-bold text-[#246990]">Événements à venir</h2>
        </div>

        <button
          onClick={togglePlayPause}
          className="p-1.5 rounded-full bg-[#246990]/10 hover:bg-[#246990]/20 text-[#246990] transition-colors"
          title={isPlaying ? 'Mettre en pause' : 'Reprendre'}
        >
          {isPlaying ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>

      {/* 🔹 Grille des événements */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {eventsByCategory.map((eventCategory, categoryIndex) => {
          if (!eventCategory || eventCategory.length === 0) {
            return (
              <div key={categoryIndex} className="h-24 relative overflow-hidden">
                <div className="bg-gradient-to-r from-gray-300 to-gray-400 p-3 rounded-lg text-white text-sm h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-semibold text-xs">Catégorie {categoryIndex + 1}</div>
                    <div className="opacity-90 text-xs mt-1">Aucun événement</div>
                  </div>
                </div>
              </div>
            );
          }

          const currentEvent = eventCategory[currentIndices[categoryIndex]] || eventCategory[0];

          return (
            <div key={categoryIndex} className="h-24 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${categoryIndex}-${currentIndices[categoryIndex]}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className={`bg-gradient-to-r ${currentEvent.color_gradient} p-3 rounded-lg text-white text-sm h-full`}
                >
                  <div className="font-semibold">{currentEvent.title}</div>
                  <div className="opacity-90 text-xs mt-2">{currentEvent.date}</div>
                </motion.div>
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingEvents;
